import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpinstance } from "../../axios/api";

// Independent from ManualQuoteSlice.js (air) on purpose — ocean manual quotes
// have a different payload shape (per-container-type charges, schedules,
// free days, rate scope) so they get their own state/thunks rather than
// branching the air slice.
const initialState = {
  queryDetails: null,
  manualQuoteDetails: {},
  // When true, OceanManualQuote renders the quote read-only (KAM "view" of an
  // already-submitted manual quotation) instead of the normal create/edit form.
  viewOnly: false,
};

// Endpoint prefix confirmed as "ocean-manual-quote" (not "manual-quote/ocean",
// which is what these were originally guessed as) by the real
// /api/ocean-manual-quote/get-locations endpoint used for searchOceanPortLocations.
export const addOceanManualQuote = createAsyncThunk(
  "addOceanManualQuote",
  async (value, { rejectWithValue }) => {
    try {
      const response = await httpinstance.post("ocean-manual-quote", value);
      return response?.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

export const getAllOceanManualQuoteById = createAsyncThunk(
  "getAllOceanManualQuoteById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await httpinstance.get(`ocean-manual-quote/get-manual-quote/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

export const getOceanManualQuoteDetailById = createAsyncThunk(
  "getOceanManualQuoteDetailById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await httpinstance.get(`ocean-manual-quote/get-manual-quote-detail/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

export const editOceanManualQuote = createAsyncThunk(
  "editOceanManualQuote",
  async (value, { rejectWithValue }) => {
    try {
      const response = await httpinstance.put(`ocean-manual-quote/${value.manualqueryId}`, value);
      return response?.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

// Version history for the read-only view — mirrors the air ManualQuoteSlice's
// getManualQuoteHistory. GET ocean-manual-quote/get-manual-quote-history/:oceanManualQuoteId
// (the OceanManualQuote's own _id), newest-first, each entry a { version, action,
// updatedBy, updatedAt, snapshot } record.
export const getOceanManualQuoteHistory = createAsyncThunk(
  "getOceanManualQuoteHistory",
  async (oceanManualQuoteId, { rejectWithValue }) => {
    try {
      const response = await httpinstance.get(
        `ocean-manual-quote/get-manual-quote-history/${oceanManualQuoteId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

// Type-ahead location/port search backing Door Origin, POL, Via Port and
// their destination-side equivalents — called per keystroke (debounced in
// AsyncPortSelect) instead of preloading the full port master list.
// `locationType` ("origin" | "destination") is the one param that varies per
// field; the rest are fixed defaults confirmed against a real request:
// GET ocean-manual-quote/get-locations?mode=FCL&query=mun&searchType=D2D
//   &include=LOCATION&locationType=origin&excludeICDs=false&excludeSeaPorts=false
export const searchOceanPortLocations = createAsyncThunk(
  "searchOceanPortLocations",
  async ({ query, locationType }, { rejectWithValue }) => {
    try {
      const response = await httpinstance.get("ocean-manual-quote/get-locations", {
        params: {
          mode: "FCL",
          query,
          searchType: "D2D",
          include: "LOCATION",
          locationType,
          excludeICDs: false,
          excludeSeaPorts: false,
        },
      });
      return response?.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

// Type-ahead pickup/delivery transporter search backing Pickup/Delivery
// Transporter — confirmed against a real request:
// GET ocean-manual-quote/get-service-providers?query=&limit=50&skip=0
//   &domainSubscriberId=&mode=FCL&source=addNewRate
// `domainSubscriberId` is left blank to match that example rather than
// guessed at from somewhere in app state.
export const searchOceanServiceProviders = createAsyncThunk(
  "searchOceanServiceProviders",
  async (query, { rejectWithValue }) => {
    try {
      const response = await httpinstance.get("ocean-manual-quote/get-service-providers", {
        params: {
          query,
          limit: 50,
          skip: 0,
          domainSubscriberId: "",
          mode: "FCL",
          source: "addNewRate",
        },
      });
      return response?.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

// Charge master backing the Buy/Sell charge dropdowns — same endpoint the
// air ManualQuoteSlice hits (manual-quote/get-all-sw-charges is shared
// across air/ocean); the caller filters to air_sea_type === 2 (sea) since
// the endpoint returns both air and sea charges.
export const getAllOceanSwCharges = createAsyncThunk(
  "getAllOceanSwCharges",
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpinstance.get("manual-quote/get-all-sw-charges");
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

const oceanManualQuoteSlice = createSlice({
  name: "oceanManualQuoteSlice",
  initialState,
  reducers: {
    setOceanQueryDetail(state, action) {
      // "Start a brand-new manual quote" action — always reset
      // manualQuoteDetails/viewOnly too, otherwise a quote previously opened
      // for View/Edit in this session would leak into the fresh create form.
      state.queryDetails = action.payload;
      state.manualQuoteDetails = {};
      state.viewOnly = false;
    },
    setOceanManualQuoteDetail(state, action) {
      state.queryDetails = action.payload.queryDetails;
      state.manualQuoteDetails = action.payload.manualQuoteDetails;
      state.viewOnly = !!action.payload.viewOnly;
    },
    clearOceanManualQuote(state) {
      state.queryDetails = null;
      state.manualQuoteDetails = {};
      state.viewOnly = false;
    },
  },
});

export const { setOceanQueryDetail, setOceanManualQuoteDetail, clearOceanManualQuote } =
  oceanManualQuoteSlice.actions;
export default oceanManualQuoteSlice.reducer;
